import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { Play, X, ArrowRight, Wallet, Clock, MessageCircle, Share2, Instagram, Facebook, MapPin, MonitorPlay, Globe, Heart, Star, Send, MoreHorizontal } from "lucide-react";
import { PassCard } from "@/portal/loyalty/PassCard";

/* Neuer Robbie-Imagefilm (Thomas): Spot mit Ton (Lightbox + eigene Sektion) und
   stumme Hero-Schleife. Dateien liegen web-optimiert unter /media/robbie/. */

const H = { fontFamily: "'Outfit', 'Poppins', sans-serif", fontWeight: 800 };
const B = { fontFamily: "'Poppins', sans-serif" };
const CREME = "#FAF8F3";
const NEON = "#FFD60A";

export const SPOT_MP4 = "/media/robbie/spot.mp4";
export const SPOT_WEBM = "/media/robbie/spot.webm";
export const SPOT_POSTER = "/media/robbie/spot-poster.webp";

/* Demo-Betrieb für die Beispielkarten (keine echten Kundendaten). Verlinkt auf die
   bestehende, öffentliche Demo-Kundenkarte /k vom Demo-Café. */
const DEMO_K = "/k/robbies-kaffeekueche-pass";
const DEMO_SHOP = {
  name: "Robbies Kaffeeküche",
  brand: { style: "espresso", color: "#8a5a2b", logo_url: "/demo/cafe_logo.webp", cover_url: "/demo/cafe_interior.webp" },
  pass: { goal: 10, icon: "coffee", reward: "1 Kaffee gratis" },
};

/* Offizielle, unveränderte deutsche Wallet-Badges (Anbieter-Assets, keine Nachbauten). */
const GOOGLE_BADGE = "/wallet/google-wallet-de.svg";
const APPLE_BADGE = "/wallet/apple-wallet-de.svg";

const CHAPTERS = [
  { t: 0, time: "0:00", icon: MessageCircle, label: "Ein Foto per WhatsApp", desc: "Du schickst Robbie ein Foto, fertig." },
  { t: 3, time: "0:03", icon: Share2, label: "Überall gesehen", desc: "Instagram, Facebook, Google-Profil, Schaufenster-Bildschirm und Mini-Website." },
  { t: 12, time: "0:12", icon: Wallet, label: "Kundenkarte & Tickets", desc: "Digitale Stempelkarte im Apple & Google Wallet, Einlass per QR-Scan." },
  { t: 18, time: "0:18", icon: Clock, label: "Mehr Zeit fürs Wesentliche", desc: "Robbie erledigt den Rest – du tippst nur „Freigeben“." },
];

const PLATTFORMEN = [
  { icon: Instagram, label: "Instagram" },
  { icon: Facebook, label: "Facebook" },
  { icon: MapPin, label: "Google-Profil" },
  { icon: MonitorPlay, label: "Bildschirm" },
  { icon: Globe, label: "Mini-Website" },
];

const up = (d = 0) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] } });

/* ---------- Lightbox: voller Spot MIT Ton, startet beim Öffnen (Klick = User-Geste) ---------- */
export const SpotLightbox = ({ open, onOpenChange, returnFocusRef }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!open || !v) return undefined;
    try { v.currentTime = 0; v.muted = false; } catch { /* egal */ }
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    return () => { try { v.pause(); } catch { /* egal */ } };
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[300] bg-[#0B0B0B]/92 backdrop-blur-md data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" data-testid="spot-lightbox-backdrop" />
        <Dialog.Content
          onCloseAutoFocus={(e) => { if (returnFocusRef?.current) { e.preventDefault(); returnFocusRef.current.focus(); } }}
          className="fixed inset-0 z-[301] flex items-center justify-center p-0 outline-none sm:p-6 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          data-testid="spot-lightbox">
          <Dialog.Title className="sr-only">Robbie in 30 Sekunden</Dialog.Title>
          <Dialog.Description className="sr-only">Der Robbie-Imagefilm mit Ton.</Dialog.Description>
          <Dialog.Close
            className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Video schließen" data-testid="spot-lightbox-close">
            <X size={20} strokeWidth={2.5} />
          </Dialog.Close>
          {open && (
            <video ref={ref} controls autoPlay playsInline preload="auto" poster={SPOT_POSTER}
              aria-label="Robbie in 30 Sekunden"
              className="h-full max-h-[100dvh] w-full bg-black object-contain sm:h-auto sm:max-h-[90dvh] sm:w-auto sm:max-w-[min(100%,calc(90dvh*9/16))] sm:rounded-[24px] sm:shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]"
              data-testid="spot-lightbox-video">
              <source src={SPOT_WEBM} type="video/webm" />
              <source src={SPOT_MP4} type="video/mp4" />
            </video>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/* ---------- Kleine „Beispiel“-Plakette für die Demo-Karten ---------- */
const BeispielPill = ({ pos = "left-3 top-3" }) => (
  <span className={`absolute ${pos} z-10 inline-flex items-center gap-1 rounded-full border-2 border-[#111] bg-[#FFD60A] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#111] shadow-[2px_2px_0_0_#111]`} data-testid="beispiel-pill">
    Beispiel
  </span>
);

/* ---------- Sektion „Robbie in 30 Sekunden“ (direkt unter dem Hero) ---------- */
export const RobbieSpotSection = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(-1);
  const [just, setJust] = useState(false);

  // Sanft animierter „neuer Stempel“ auf der Demo-Kundenkarte.
  useEffect(() => {
    const id = setInterval(() => { setJust(true); setTimeout(() => setJust(false), 600); }, 3600);
    return () => clearInterval(id);
  }, []);

  const startAt = (t, i) => {
    const v = videoRef.current;
    if (!v) return;
    setPlaying(true);
    setActive(i);
    try { v.muted = false; v.currentTime = t; } catch { /* egal */ }
    const p = v.play();
    if (p && p.catch) p.catch(() => setPlaying(false));
  };

  const onTime = () => {
    const v = videoRef.current;
    if (!v) return;
    const tc = v.currentTime;
    let idx = 0;
    for (let i = 0; i < CHAPTERS.length; i++) if (tc >= CHAPTERS[i].t - 0.1) idx = i;
    setActive(idx);
  };

  const hideBadge = (e) => { const a = e.currentTarget.closest("a"); if (a) a.style.display = "none"; };

  return (
    <section id="robbie-film" className="scroll-mt-24" style={{ background: CREME }} data-testid="robbie-spot-section">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-8 px-6 py-10 md:px-10 md:py-12 lg:grid lg:items-start lg:gap-x-16 lg:gap-y-6 lg:[grid-template-columns:minmax(0,1fr)_360px] lg:[grid-template-areas:'head_film'_'chapters_film'_'cards_cards'_'cta_cta']">

        {/* Kopf */}
        <motion.div {...up(0)} className="flex flex-col items-start lg:[grid-area:head]">
          <span style={{ ...B, fontWeight: 700 }} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#111]" data-testid="robbie-spot-eyebrow">
            <Play size={12} strokeWidth={2.8} fill="currentColor" aria-hidden="true" /> Imagefilm
          </span>
          <h2 style={H} className="mt-3 text-3xl leading-tight text-[#111] md:text-4xl" data-testid="robbie-spot-headline">
            Robbie in <span className="box-decoration-clone bg-[#FFD60A] px-2">30 Sekunden.</span>
          </h2>
          <p style={B} className="mt-3 max-w-[46ch] text-[16px] leading-relaxed text-[#4A4A4A]" data-testid="robbie-spot-lead">
            Schau zu, wie aus einem einzigen Foto Posts, ein Google-Beitrag, deine Kundenkarte und dein Schaufenster-Bildschirm werden – in einer halben Minute.
          </p>
        </motion.div>

        {/* Film – rechts, auf Desktop sticky (im eigenen Rasterbereich) während man die Kapitel liest */}
        <motion.div {...up(0.1)} className="flex justify-center lg:block lg:self-stretch lg:[grid-area:film]">
          <div className="lg:sticky lg:top-24">
            <div className="relative mx-auto aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-[28px] border-2 border-[#111] bg-black shadow-[8px_8px_0_0_#111]" data-testid="spot-section-player">
            <video ref={videoRef} poster={SPOT_POSTER} controls={playing} playsInline preload="none"
              className="h-full w-full object-cover" data-testid="spot-section-video"
              onTimeUpdate={onTime} onEnded={() => setPlaying(false)}>
              <source src={SPOT_WEBM} type="video/webm" />
              <source src={SPOT_MP4} type="video/mp4" />
            </video>
            {!playing && (
              <button type="button" onClick={() => startAt(0, 0)} aria-label="Robbie-Imagefilm abspielen" data-testid="spot-section-play"
                className="group absolute inset-0 grid place-items-center bg-black/20 transition-colors hover:bg-black/30 focus:outline-none">
                <span className="grid h-20 w-20 place-items-center rounded-full border-2 border-[#111] text-[#111] shadow-[0_6px_0_0_#111] transition-transform group-hover:scale-105" style={{ background: NEON }}>
                  <Play size={34} strokeWidth={2.5} fill="currentColor" className="ml-1" />
                </span>
              </button>
            )}
            </div>
          </div>
        </motion.div>

        {/* Kapitel-Liste – neben dem Film, klickbar, springt zur Stelle */}
        <motion.ol {...up(0.15)} className="flex flex-col gap-2 lg:[grid-area:chapters]" data-testid="robbie-chapters">
          {CHAPTERS.map((c, i) => {
            const Icon = c.icon;
            const on = active === i;
            return (
              <li key={c.t}>
                <button type="button" onClick={() => startAt(c.t, i)} data-testid={`robbie-chapter-${i}`}
                  className={`flex w-full items-start gap-3 rounded-2xl border-2 border-[#111] p-2.5 text-left transition-all ${on ? "bg-[#FFD60A] shadow-[4px_4px_0_0_#111]" : "bg-white shadow-[3px_3px_0_0_#111] hover:-translate-y-0.5"}`}>
                  <span style={{ ...B, fontWeight: 700 }} className="mt-0.5 inline-flex shrink-0 items-center rounded-lg border-2 border-[#111] bg-white px-2 py-0.5 text-[12px] font-bold tabular-nums text-[#111]">{c.time}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <Icon size={17} strokeWidth={2.4} className="shrink-0 text-[#111]" aria-hidden="true" />
                      <span style={{ ...B, fontWeight: 700 }} className="text-[15px] font-bold leading-tight text-[#111]">{c.label}</span>
                      {on && playing && <span className="ml-auto inline-flex h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#111]" aria-hidden="true" />}
                    </span>
                    <span style={B} className="mt-1 block text-[13px] leading-snug text-[#4A4A4A]">{c.desc}</span>
                    {i === 1 && (
                      <span className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="robbie-chapter-platforms">
                        {PLATTFORMEN.map((p) => {
                          const PI = p.icon;
                          return <span key={p.label} title={p.label} className="grid h-7 w-7 place-items-center rounded-full border-2 border-[#111] bg-white shadow-[1px_1px_0_0_#111]"><PI size={13} strokeWidth={2.2} className="text-[#111]" aria-hidden="true" /><span className="sr-only">{p.label}</span></span>;
                        })}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </motion.ol>

        {/* Live-Beispielkarten */}
        <motion.div {...up(0.1)} className="lg:[grid-area:cards]" data-testid="robbie-demo-cards">
          <div className="mb-4">
            <h3 style={H} className="text-2xl leading-tight text-[#111]">So sieht Robbies Arbeit aus.</h3>
            <p style={B} className="mt-1.5 text-[13px] text-[#4A4A4A]">Beispiele vom Demo-Betrieb – keine echten Kundendaten.</p>
          </div>
          <div className="-mx-6 flex snap-x snap-mandatory items-start gap-4 overflow-x-auto px-6 pb-4 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3">

            {/* Karte 1 – Demo-Kundenkarte (Wallet-Stil) */}
            <article className="relative flex w-[78%] shrink-0 snap-start flex-col sm:w-auto" data-testid="demo-card-kundenkarte">
              <BeispielPill />
              <div className="mx-auto w-full max-w-[230px]">
                <PassCard shop={DEMO_SHOP} stamps={3} justStamped={just} testId="robbie-demo-passcard" />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5" data-testid="demo-card-wallet-badges">
                <Link to={DEMO_K} aria-label="Zu Google Wallet hinzufügen – Demo-Karte öffnen" data-testid="demo-google-badge" className="transition-transform hover:-translate-y-px">
                  <img src={GOOGLE_BADGE} alt="In Google Wallet speichern" className="block h-[40px] w-auto" />
                </Link>
                <Link to={DEMO_K} aria-label="Zu Apple Wallet hinzufügen – Demo-Karte öffnen" data-testid="demo-apple-badge" className="transition-transform hover:-translate-y-px">
                  <img src={APPLE_BADGE} alt="Zu Apple Wallet hinzufügen" className="block h-[40px] w-auto" onError={hideBadge} />
                </Link>
              </div>
              <p className="mt-2 text-center text-[11px] text-black/45">Demo – keine echten Kundendaten</p>
            </article>

            {/* Karte 2 – Google-Beitrag-Vorschau */}
            <article className="relative flex w-[78%] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border-2 border-[#111] bg-white shadow-[4px_4px_0_0_#111] sm:w-auto" data-testid="demo-card-google">
              <BeispielPill />
              <img src="/demo/latte_art.webp" alt="Flat White – Beispiel-Beitrag" className="h-32 w-full object-cover" />
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center gap-2">
                  <span className="grid h-7 w-7 place-items-center rounded-full border border-black/10 bg-white text-[14px] font-black" style={{ color: "#4285F4" }} aria-hidden="true">G</span>
                  <div className="leading-tight">
                    <p style={{ ...B, fontWeight: 700 }} className="text-[13px] font-bold text-[#111]">Robbies Kaffeeküche</p>
                    <p className="text-[11px] text-black/50">Google-Unternehmensprofil · Beitrag</p>
                  </div>
                </div>
                <p style={B} className="mt-3 text-[13px] leading-relaxed text-[#333]">Frisch gebrüht: unsere neue Hausröstung als Flat White – heute bei uns bis 18 Uhr.</p>
                <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full border border-black/15 px-3 py-1 text-[12px] font-semibold" style={{ color: "#1a73e8" }}>Mehr erfahren</span>
                <p className="mt-3 flex items-center gap-1.5 text-[11px] text-black/45"><Share2 size={12} strokeWidth={2.2} aria-hidden="true" /> automatisch von Robbie veröffentlicht</p>
              </div>
            </article>

            {/* Karte 3 – Instagram-/Schaufenster-Vorschau */}
            <article className="relative flex w-[78%] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border-2 border-[#111] bg-white shadow-[4px_4px_0_0_#111] sm:w-auto" data-testid="demo-card-instagram">
              <BeispielPill pos="right-3 top-[52px]" />
              <div className="flex items-center gap-2 p-3">
                <img src="/demo/cafe_logo.webp" alt="" className="h-7 w-7 rounded-full border border-black/10 object-cover" />
                <p style={{ ...B, fontWeight: 700 }} className="flex-1 text-[13px] font-bold text-[#111]">robbies.kaffeekueche</p>
                <MoreHorizontal size={18} className="text-black/40" aria-hidden="true" />
              </div>
              <img src="/demo/latte_art.webp" alt="Latte Art – Beispiel-Post" className="h-44 w-full object-cover" />
              <div className="flex items-center gap-4 px-3 py-2.5">
                <Heart size={20} strokeWidth={2} className="text-[#111]" aria-hidden="true" />
                <MessageCircle size={20} strokeWidth={2} className="text-[#111]" aria-hidden="true" />
                <Send size={20} strokeWidth={2} className="text-[#111]" aria-hidden="true" />
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-black/50"><Instagram size={13} strokeWidth={2.2} aria-hidden="true" /> Instagram</span>
              </div>
              <p className="px-3 pb-4 text-[12px] text-black/60"><span style={{ ...B, fontWeight: 700 }} className="font-bold text-[#111]">robbies.kaffeekueche</span> Hausröstung, frisch im Glas.</p>
            </article>

          </div>
        </motion.div>

        {/* CTA + Demo-Bereich + Kernzeile + Vertrauenszeile */}
        <motion.div {...up(0.1)} className="flex flex-col items-start lg:[grid-area:cta]">
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/kunden/registrieren" data-testid="robbie-spot-cta" style={{ ...B, fontWeight: 700 }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#111] bg-[#FFD60A] px-6 py-3.5 text-[15px] font-bold text-[#111] shadow-[0_6px_0_0_#111] transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0_3px_0_0_#111]">
              30 Tage kostenlos testen <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
            <Link to="/live-demo" data-testid="robbie-spot-demo-bereich" style={{ ...B, fontWeight: 700 }}
              onClick={() => { try { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/demo-cta`, { method: "POST", headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" }, body: JSON.stringify({ quelle: "startseite-robbiespot" }) }); } catch { /* egal */ } }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#111] bg-white px-6 py-3.5 text-[15px] font-bold text-[#111] transition-transform hover:-translate-y-0.5 active:translate-y-0">
              <Play size={16} strokeWidth={2.5} fill="currentColor" /> Demo-Bereich anschauen
            </Link>
          </div>
          <p style={{ ...B, fontWeight: 700 }} className="mt-4 text-[14px] text-[#111]" data-testid="robbie-spot-kreis">
            Gesehen <span className="text-black/25" aria-hidden="true">·</span> Gekommen <span className="text-black/25" aria-hidden="true">·</span> Wiedergekommen
          </p>
          <div style={B} className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] font-semibold text-[#4A4A4A]" data-testid="robbie-spot-trust">
            <span className="inline-flex items-center gap-1"><span className="text-[#111]">5,0</span> <Star size={14} fill={NEON} className="text-[#FFD60A]" aria-hidden="true" /> bei Google</span>
            <span className="text-black/20" aria-hidden="true">·</span>
            <span><span className="text-[#111]">250.000+</span> Instagram-Aufrufe</span>
            <span className="text-black/20" aria-hidden="true">·</span>
            <span>seit <span className="text-[#111]">2000</span></span>
          </div>
        </motion.div>

      </div>
    </section>
  );
};
